import type { FC, ReactNode } from "react";
import { useCallback, useState } from "react";

import type { Dayjs } from "dayjs";

import type { IPopupConfig } from "@similarweb/ui-components";

import {
    CLOSE_DURATION_FILTER,
    OPEN_DURATION_FILTER,
} from "app/components/duration-selector/durationFilterActionTypes";
import CalendarStep from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/steps/CalendarStep/CalendarStep";
import DropdownStep from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/steps/DropdownStep/DropdownStep";
import { DurationSelectorButton } from "pro-features/components/duration-selectors/DurationSelectorButton";
import ConnectedPopup from "app/components/duration-selector/ConnectedPopup";
import { convertInitialPresetToDuration } from "app/components/duration-selector/DurationSelectorUtils";
import type { IndicatorType, Presets } from "./types";
import { IndicatorTypes } from "./types";
import { allTrackers } from "app/services/track/track";

enum Steps {
    DROPDOWN = "DROPDOWN",
    CALENDAR = "CALENDAR",
}

interface IDurationSelectorPresetOriented {
    onSubmit: (duration: any, comparedDuration: any) => void;
    minDate: Dayjs;
    maxDate: Dayjs;
    height?: number;
    isDisabled?: boolean;
    presets: Presets;
    initialComparedDuration?: string | boolean;
    appendTo?: string;
    initialPreset: string;
    children?: ReactNode;
    disableCalendar?: boolean;
    showWeeklyToggle?: boolean;
    isWeeklyDataAvailable?: boolean;
    weeklyToggleProps?: {
        weekly?: ReactNode;
        monthly?: ReactNode;
    };
    onGranularityToggleClick?: (overrideTrackingKey: string) => void;
    presentNotificationIndicator?: boolean;
    presentNotificationTooltipKey?: string;
    indicatorType?: IndicatorType;
}

export const DurationSelectorPresetOriented: FC<IDurationSelectorPresetOriented> = ({
    isDisabled = false,
    height = 60,
    appendTo = "body",
    minDate,
    maxDate,
    initialPreset,
    presets,
    onSubmit,
    disableCalendar = false,
    children,
    showWeeklyToggle,
    isWeeklyDataAvailable,
    weeklyToggleProps,
    onGranularityToggleClick,
    presentNotificationIndicator,
    presentNotificationTooltipKey,
    indicatorType = IndicatorTypes.NONE,
}) => {
    const [step, setStep] = useState(Steps.DROPDOWN);
    const toDropdownStep = useCallback(() => setStep(Steps.DROPDOWN), []);
    const toCalendarStep = useCallback(() => {
        setStep(Steps.CALENDAR);
        allTrackers.dispatchTrackEvent("Drop Down", "click", "Header/Date range/open/custom");
    }, []);

    const [selectedPreset, setSelectedPreset] = useState<string | null>(
        initialPreset?.indexOf("-") !== -1 ? null : initialPreset,
    );
    const availablePresets = presets.filter((p) => !p.disabled && !p.locked);
    const unavailablePresets = presets.filter((p) => p.locked);
    const initialDuration = convertInitialPresetToDuration(initialPreset, [...availablePresets]);

    const config: IPopupConfig = {
        placement: "bottom-right",
        growsTo: "left",
        animationDelay: 0,
        autoPlacement: false,
        cssClassContainer: `DropdownButton-popup-container DurationSelector-popup-container`,
        enabled: !isDisabled,
    };

    const onPresetSelect = useCallback(
        (id: string) => {
            onSubmit(id, false);
            setSelectedPreset(id);
        },
        [onSubmit, setSelectedPreset],
    );

    const onCustomRangeSubmit = useCallback(
        (duration: any) => {
            onSubmit(duration, false);
            setSelectedPreset(null);
        },
        [onSubmit, setSelectedPreset],
    );

    const content = () => {
        switch (step) {
            case Steps.DROPDOWN:
                return (
                    <DropdownStep
                        onPresetSelect={onPresetSelect}
                        onCustomDuration={toCalendarStep}
                        presets={[...availablePresets, ...unavailablePresets]}
                        selectedPreset={selectedPreset}
                        selectedCustomDuration={selectedPreset ? null : initialDuration}
                        disableCalendar={disableCalendar}
                        showWeeklyToggle={showWeeklyToggle}
                        isWeeklyDataAvailable={isWeeklyDataAvailable}
                        onGranularityToggleClick={onGranularityToggleClick}
                        weeklyToggleProps={weeklyToggleProps}
                    />
                );
            case Steps.CALENDAR:
                return (
                    <CalendarStep
                        onBack={toDropdownStep}
                        onSubmit={onCustomRangeSubmit}
                        minDate={minDate}
                        maxDate={maxDate}
                        initialDuration={initialDuration}
                        showWeeklyToggle={showWeeklyToggle}
                        isWeeklyDataAvailable={isWeeklyDataAvailable}
                        onGranularityToggleClick={onGranularityToggleClick}
                        weeklyToggleProps={weeklyToggleProps}
                    />
                );
            default:
                return null;
        }
    };

    const handleOpenDurationSelector = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "open", "Header/Date range/open");
    };
    return (
        <ConnectedPopup
            stateKey="WebsiteAnalysisFilters.durationFilter"
            appendTo={appendTo}
            config={config}
            content={content}
            openAction={{ type: OPEN_DURATION_FILTER }}
            closeAction={{ type: CLOSE_DURATION_FILTER }}
            onClose={toDropdownStep}
            onOpen={handleOpenDurationSelector}
        >
            <div>
                {children || (
                    <DurationSelectorButton
                        isDisabled={isDisabled}
                        height={height}
                        presets={availablePresets}
                        initialPreset={initialPreset}
                        presentNotificationIndicator={presentNotificationIndicator}
                        presentNotificationTooltipKey={presentNotificationTooltipKey}
                        indicatorType={indicatorType}
                    />
                )}
            </div>
        </ConnectedPopup>
    );
};
